import { useCallback, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { Buffer } from "buffer";
// UI
import { notifications } from "@mantine/notifications";
//
import {
  SurveyEventBuilder,
  type SurveyEngine,
  type Step,
  // events
  type StepSubmittedEvent,
  // request/response
  type StepSubmissionHandlerRespBody,
  type AnySurveyEngineEvent,
  type StandardSurveyEngineAnalytics,
} from "@neuromotion-inc/mightier-survey-engine";

//
import { backendApi } from "../../backendApi/backendApi";
import { isAxiosError } from "axios";

/**
 * UTILS
 */

const logErrToSentry = (
  error: Error,
  eventPayload: AnySurveyEngineEvent,
): void => {
  const { sessionId, surveyInstanceUuid, surveyVersion, surveyId } =
    eventPayload.context;

  const currentStepId =
    "currentStepId" in eventPayload.context
      ? eventPayload.context.currentStepId
      : "N/A";

  Sentry.captureMessage("Failed to send survey analytic event", {
    extra: {
      error,
      sessionId,
      surveyInstanceUuid,
      currentStepId,
      surveyVersion,
      surveyId,
    },
  });
  Sentry.captureException(error);
};

/**
 * dispatches 'mightier-survey-engine-event' to `window` object
 * so that parentApp can subscribe when rendering this page in a webview.
 *
 * Only dispatches if the window object has a 'ReactNativeWebView' property
 *
 * @param eventDetail the payload that's available for subscribers in CustomEvent.detail
 */
const dispatchSurveyEngineEventToWindow = (
  eventDetail: AnySurveyEngineEvent,
): void => {
  if ("ReactNativeWebView" in window && window.ReactNativeWebView) {
    window.dispatchEvent(
      new CustomEvent("mightier-survey-engine-event", { detail: eventDetail }),
    );
  }
};

/**
 * MUTATION
 */

interface SendAnalyticMutationVariables {
  eventPayload: StandardSurveyEngineAnalytics;
}

type SendAnalyticMutationResult = UseMutationResult<
  void,
  Error,
  SendAnalyticMutationVariables
>;

/**
 * We use a mutation here because we want to send the analytic event in the background.
 */
const useSendAnalyticMutation = (): SendAnalyticMutationResult => {
  return useMutation({
    mutationKey: ["SURVEY_ENGINE_ANALYTIC"],
    mutationFn: async (variables: SendAnalyticMutationVariables) => {
      await backendApi.trackSurveyAnalytic(variables.eventPayload);
      dispatchSurveyEngineEventToWindow(variables.eventPayload);
    },
    onError: (error, variables) => {
      logErrToSentry(error, variables.eventPayload);
    },
  });
};

interface SubmitStepMutationVariables {
  eventPayload: StepSubmittedEvent;
}

type SubmitStepMutationResult = UseMutationResult<
  StepSubmissionHandlerRespBody,
  Error,
  SubmitStepMutationVariables
>;

const useSubmitStepMutation = (
  engine: SurveyEngine,
): SubmitStepMutationResult => {
  const { t } = useTranslation();
  const onSuccessfulSubmit = useCallback(
    (resp: StepSubmissionHandlerRespBody) => {
      // pass additional context to survey engine :)
      engine.addContext(resp.additionalEngineContext);
    },
    [engine],
  );

  const onError = useCallback(
    (error: Error, variables: SubmitStepMutationVariables) => {
      logErrToSentry(error, variables.eventPayload);

      if (isAxiosError<StepSubmissionHandlerRespBody>(error)) {
        const respData = error.response?.data;

        if (!respData) {
          return;
        }

        if (respData.errors.length > 0) {
          const message = respData.errors.map((e) => e.message).join("\n");

          notifications.show({
            title: t("survey.errorMessages.failedToSubmitStep"),
            message,
            color: "red",
          });
        }
      }
    },
    [t],
  );

  return useMutation({
    mutationKey: ["SURVEY_ENGINE_SUBMIT_STEP"],
    mutationFn: async (variables: SubmitStepMutationVariables) => {
      const resp = await backendApi.submitSurveyStep({
        event: variables.eventPayload,
      });

      return resp.data;
    },
    onSuccess: onSuccessfulSubmit,
    onError,
  });
};

/**
 * ACCESSOR HOOK
 */

type EventBuilderContext = ConstructorParameters<typeof SurveyEventBuilder>[2];

export type SurveyEventAdditionalContext = Omit<
  EventBuilderContext,
  "locale" | "meta" | "userId"
>;

interface UseSurveyAnalytics {
  performStepSubmission: (
    stepId: string,
    context: SurveyEventAdditionalContext,
  ) => Promise<void>;
  sendAnalyticInBackground: SendAnalyticMutationResult["mutate"];
}

export const useSurveyAnalytic = (
  surveyEngine: SurveyEngine,
  meta: string,
): UseSurveyAnalytics => {
  const { i18n, t } = useTranslation("mightier_survey_lib");

  const { mutate: sendAnalyticInBackground } = useSendAnalyticMutation();
  const { mutateAsync: submitStepAsync, mutate: submitStepInBackground } =
    useSubmitStepMutation(surveyEngine);

  const locale = useMemo(() => i18n.language, [i18n.language]);

  const userId = useMemo(() => {
    const parsed = JSON.parse(Buffer.from(meta, "base64").toString("utf-8"));
    return parsed.user_id;
  }, [meta]);

  const createEventBuilder = useCallback(
    (context: SurveyEventAdditionalContext) => {
      return new SurveyEventBuilder(surveyEngine, t, {
        locale,
        meta,
        userId,
        ...context,
      });
    },
    [surveyEngine, t, locale, meta, userId],
  );

  const performStepSubmission = useCallback(
    async (stepId: string, context: SurveyEventAdditionalContext) => {
      const eventBuilder = createEventBuilder(context);

      const eventPayload = eventBuilder.buildStepSubmittedEvent(stepId);

      const hasRpcs = eventPayload.context.results.some(
        (stepResult) => stepResult.question.remote_procedure_calls.length > 0,
      );

      if (!hasRpcs) {
        submitStepInBackground({ eventPayload });
        dispatchSurveyEngineEventToWindow(eventPayload);
        return;
      }

      await submitStepAsync({ eventPayload });
      dispatchSurveyEngineEventToWindow(eventPayload);
    },
    [createEventBuilder, submitStepAsync, submitStepInBackground],
  );

  return useMemo(() => {
    return {
      performStepSubmission,
      sendAnalyticInBackground,
    };
  }, [performStepSubmission, sendAnalyticInBackground]);
};

export const useTrackingQuestionsViewed = (
  currentStep: Step,
  eventBuilder: SurveyEventBuilder,
  sendAnalytic: UseSurveyAnalytics["sendAnalyticInBackground"],
): void => {
  // set to undefined at first render, because surveyId is present at first render
  // (thanks to tanstack router loading component).
  // useEffect runs on mount, so the ref changes from `undefined` to `string` immediately
  const curStepIdRef = useRef<string>();

  useEffect(() => {
    if (curStepIdRef.current !== currentStep.id) {
      curStepIdRef.current = currentStep.id;

      currentStep.sections.forEach((section) => {
        if (section.type === "QUESTION") {
          const p = eventBuilder.buildQuestionViewedEvent(section.content.id);
          sendAnalytic({ eventPayload: p });
        }
      });
    }
  }, [currentStep, eventBuilder, sendAnalytic]);
};

export const useTrackSurveyLoaded = (
  surveyId: string,
  eventBuilder: SurveyEventBuilder,
  sendAnalytic: UseSurveyAnalytics["sendAnalyticInBackground"],
): void => {
  // set to undefined at first render, because surveyId is present at first render
  // (thanks to tanstack router loading component).
  // useEffect runs on mount, so the ref changes from `undefined` to `string` immediately
  const lastSurveyIdThatAnalyticWasSentFor = useRef<string>();

  useEffect(() => {
    // prevent dupe tracking for save survey id. other things may re-render this hook
    // and we don't want to send the same analytic again unless ID has changed
    if (surveyId === lastSurveyIdThatAnalyticWasSentFor.current) {
      return;
    }

    lastSurveyIdThatAnalyticWasSentFor.current = surveyId;
    sendAnalytic({
      eventPayload: eventBuilder.buildInstanceLoadedEvent(),
    });
  }, [surveyId, eventBuilder, sendAnalytic]);
};
